<template>
  <div
    class="image d-flex flex-column align-items-center justify-content-center"
    :style="style"
  >
    <template v-if="!hideOverlay">
      <div class="tag tag--top-brand" v-if="media.is_new">
        {{ $t('PRODUCTS.LABELS.NEW') }}
      </div>
      <div
        class="d-flex align-items-center justify-content-center play-container h1 c-white"
      >
        <div v-if="isLocked" class="locked-icon">
          <i class="fa fa-lock-alt"></i>
        </div>
      </div>
      <div
        class="tag tag--type-icon"
        v-if="mediaIconsEnabled && getMediaTypeIcon"
      >
        <i :class="getMediaTypeIcon"></i>
      </div>
      <div class="tag tag--time" v-if="mediaTimeEnabled && duration">
        {{ duration }}
      </div>
    </template>
  </div>
</template>

<script lang="ts">

import {
  getMediaTypeIconClasses,
  formatVideoLengthFromSeconds,
  getDefaultThumbnailForMediaType,
} from '@/utils/media';
import { mapState } from 'pinia';
import { useProductStore } from '@/stores';

export default {
  name: 'MediaThumbnail',
  props: {
    media: {
      type: Object as () => ProductMediaResponse,
      required: false,
    },
    hideOverlay: {
      type: Boolean,
      default: false,
    },
    mediaTypeOverride: {
      type: String,
    },
    isLocked: {
      type: Boolean,
      default: false,
    },
    mediaIconsEnabled: {
      type: Boolean,
      default: true,
    },
    mediaTimeEnabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState(useProductStore, {
      canViewProductContent: 'canViewProductContent',
    }),
    duration() {
      const videoLength = this.media?.media?.video_length;
      if (!videoLength) return null;
      return formatVideoLengthFromSeconds(videoLength);
    },
    getMediaTypeIcon() {
      if (!this.media) return '';
      return getMediaTypeIconClasses(this.media?.media?.file_content);
    },
    style(): object | null {
      const thumbnailUrl =
        this.media?.media?.thumbnail ||
        getDefaultThumbnailForMediaType(
          this.media?.media?.file_content || this.mediaTypeOverride
        );
      return {
        backgroundImage: 'url(' + thumbnailUrl + ')',
        backgroundSize: 'cover',
      };
    },
  },
  methods: {},
};
</script>

<style lang="scss" scoped>
@import '@/styles/components-variables.scss';

$tags-offset: 8px;
$tag-height: 26px;

.inner-container {
  min-width: 66%;
  width: 66%;
  position: absolute;
  bottom: 0;
  left: 0;
}
.tag {
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: $border-radius;
  color: $white;
  height: $tag-height;
  padding: 2px 9px;
  font-size: 0.75rem;

  &--top-red {
    top: $tags-offset;
    left: $tags-offset;
    background-color: $coral;
  }

  &--top-brand {
    top: $tags-offset;
    left: $tags-offset;
    background-color: var(--brand-color, $coral);
  }

  &--type-icon {
    padding: 2px;
    bottom: $tags-offset;
    left: $tags-offset;
    background-color: rgba($black, 0.5);
    width: $tag-height;
  }

  &--time {
    bottom: $tags-offset;
    left: $tags-offset * 2 + $tag-height;
    background-color: rgba($black, 0.5);
  }
}

.image {
  border-radius: 0.375rem;
  background-position: center center;
  background-size: 50% 50%;
  background-repeat: no-repeat;
  background-color: var(
    --brand-color-with-opacity-03,
    rgba($light-bubblegum, 0.3)
  );
  background-image: url('/assets/images/empty/empty-products.svg');
  object-fit: cover;
  transition: all 0.3s ease-in-out;
}
.locked-icon {
  z-index: 1;
}
</style>
